import axios from "axios";
import Map from '@/model/Map';
import MapObject from '@/model/MapObject';
import MapObjectStatus from '@/model/MapObjectStatus';
import { firestore, functions, realtimeDatabase, storage } from '@/firebaseConfig'
import UUIDUtils from '@/utils/UUIDUtils';

export default class MapService {

  loadMapJSONDefinition(bundleId, mapDefinitionFilename) {
    const promise = new Promise((resolve, reject) => {
      const url = this.buildUrlToMapBundle(bundleId) + "/" + encodeURIComponent(mapDefinitionFilename);
      axios.get(url).then((response) => {
        if (response) {

          const mapJSONDefinition = response.data;
          const mapId = mapJSONDefinition.uid;

          realtimeDatabase.ref("/mapBundles/" + bundleId + "/maps/" + mapId).once("value").then((dataSnapshot) => {
            if (dataSnapshot) {
              const dataToMerge = dataSnapshot.val();

              if (dataToMerge) {
                if (dataToMerge.styles) {
                  if (!mapJSONDefinition.styles) {
                    mapJSONDefinition.styles = [];
                  }

                  for (const styleToAdd of dataToMerge.styles) {
                    mapJSONDefinition.styles.push(styleToAdd);
                  }
                }

                if (dataToMerge.objects) {
                  if (!mapJSONDefinition.objects) {
                    mapJSONDefinition.objects = [];
                  }

                  for (const objectToAdd of dataToMerge.objects) {
                    mapJSONDefinition.objects.push(objectToAdd);
                  }
                }

                if (dataToMerge.projection && dataToMerge.projection.tiles && dataToMerge.projection.tiles.length > 0) {
                  if (mapJSONDefinition.projection && mapJSONDefinition.projection.tiles && mapJSONDefinition.projection.tiles.length > 0) {
                    mapJSONDefinition.projection.tiles[0].tileServerUrl = dataToMerge.projection.tiles[0].tileServerUrl;
                    mapJSONDefinition.projection.tiles[0].tileServerProtocol = dataToMerge.projection.tiles[0].tileServerProtocol;
                    if (dataToMerge.projection.tiles[0].maxZoom) {
                      mapJSONDefinition.projection.tiles[0].maxZoom = dataToMerge.projection.tiles[0].maxZoom;
                    }
                  }
                }

                if (dataToMerge.searchPanelStructure) {
                  mapJSONDefinition.searchPanelStructure = dataToMerge.searchPanelStructure;
                }
              }
            }

            resolve(response.data);
          }).catch((error) => {
            reject(error);
          });
        } else {
          resolve(null);
        }
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapsForPublisher(publisherId) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("maps").get().then((querySnapshot) => {
        const result = [];

        querySnapshot.forEach((doc) => {
          result.push(new Map(doc.data()));
        });

        resolve(result);
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapForPublisher(publisherId, mapId) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("maps").doc(mapId).get().then((doc) => {
        if (doc.exists) {
          resolve(new Map(doc.data()));
        } else {
          resolve(null);
        }
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapsByMapDefinitionFilename(publisherId, mapDefinitionFilename) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("maps").where("mapDefinitionFilename", "==", mapDefinitionFilename).get().then((querySnapshot) => {
        const result = [];

        querySnapshot.forEach((doc) => {
          result.push(new Map(doc.data()));
        });

        resolve(result);
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  saveMapForPublisher(publisherId, map) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("maps").doc(map.id).set(map.toJSON()).then(() => {
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapObjectsForMap(publisherId, mapId) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("mapObjects").where("mapIds." + mapId, "==", true).get().then((querySnapshot) => {
        const result = [];

        querySnapshot.forEach((doc) => {
          result.push(new MapObject(doc.data()));
        });

        resolve(result);
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapObject(publisherId, mapObjectId) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("mapObjects").doc(mapObjectId).get().then((doc) => {
        if (doc.exists) {
          resolve(new MapObject(doc.data()));
        } else {
          resolve(null);
        }
      }).catch((error) => {
        reject(error);
      })
    });

    return promise;
  }

  async loadMapObjectsOfType(user, publisherId, mapId, poiType) {
    if (publisherId && mapId) {
      const querySnapshot = await firestore.collection("publishers").doc(publisherId).collection("mapObjects")
        .where("mapIds." + mapId, "==", true).where("poiType", "==", poiType).get();
      const result = [];
      const tempDocs = []
      querySnapshot.forEach((doc) => {
        tempDocs.push(doc.data());
      })
      for (const docData of tempDocs) {
        result.push(new MapObject(docData));
      }
      return result;
    } else {
      return [];
    }
  }

  async loadMapObjectForm(publisherId, mapObject) {
    const formSnapshot = await firestore.collection("publishers").doc(publisherId)
      .collection('forms').where("qualifiers." + mapObject.poiType, "==", true).get();
    const forms = [];
    formSnapshot.forEach(form => {
      forms.push(form.data())
    });

    if (forms.length > 0) {
      // inject the forms
      mapObject.forms = {}
      forms.forEach(form => {
        mapObject.forms[form.id] = form
      })

      const formValues = await firestore.collection("publishers").doc(publisherId)
        .collection("mapObjects").doc(mapObject.id)
        .collection("formFieldValues").get()

      formValues.forEach((values) => {
        const formValueData = values.data();
        if (formValueData.formId in mapObject.forms) {
          const form = mapObject.forms[formValueData.formId]
          form.formFields = form.formFields.map((field) => {
            if (formValueData.formFieldId === field.id) {
              return { ...field, value: formValueData.value }
            }
            return field
          })
        }
      });
    }

    return mapObject

  }


  async loadMapObjectMedia(publisherId, mapObject) {
    const medias = await firestore
      .collection("publishers")
      .doc(publisherId)
      .collection("mapObjects")
      .doc(mapObject.id)
      .collection("media")
      .get()

    if (medias) {
      const mediaPaths = []
      medias.forEach((container) => {
        const media = container.data()
        const mediaPath = "publishers/" + publisherId + "/mapObjects/" + mapObject.id + "/media/" + media.filename;
        mediaPaths.push(mediaPath)
      })
      const mediaUrls = []
      for (const path of mediaPaths) {
        const url = await storage.ref(path).getDownloadURL()
        mediaUrls.push(`${url}.jpg`)
      }
      mapObject.images = mediaUrls;
    }

    return mapObject

  }

  saveMapObject(publisherId, mapObject) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("mapObjects").doc(mapObject.id).set(mapObject.toJSON()).then(() => {
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  saveMapObjects(publisherId, mapObjects) {
    const promise = new Promise((resolve, reject) => {
      const allPromises = [];

      for (var mapObject of mapObjects) {
        allPromises.push(this.saveMapObject(publisherId, mapObject));
      }

      Promise.all(allPromises).then(() => {
        resolve();
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  loadMapObjectStatusForMap(publisherId, mapId) {
    const promise = new Promise((resolve, reject) => {
      firestore.collection("publishers").doc(publisherId).collection("mapObjectStatus").where("mapIds." + mapId, "==", true).get().then((querySnapshot) => {
        const result = [];

        querySnapshot.forEach((doc) => {
          result.push(new MapObjectStatus(doc.data()));
        });

        resolve(result);
      }).catch((error) => {
        reject(error);
      });
    });

    return promise;
  }

  listeningToMapObjectStatusForMap(publisherId, mapId, availableOnMap, callback) {
    return firestore.collection("publishers").doc(publisherId).collection("mapObjectStatus").where("mapIds." + mapId, "==", true).onSnapshot((querySnapshot) => {
      const result = [];
      querySnapshot.forEach((doc) => {
        result.push(new MapObjectStatus(doc.data(), availableOnMap));
      });

      callback(result);
    });
  }

  saveMapObjectStatusArray(publisherId, mapObjectStatusArray) {
    if (publisherId && mapObjectStatusArray) {
      const promise = new Promise((resolve, reject) => {
        const batch = firestore.batch();

        for (var mapObjectStatus of mapObjectStatusArray) {
          if (!mapObjectStatus.id) {
            mapObjectStatus.id = UUIDUtils.generateUUID();
          }

          firestore.collection("publishers").doc(publisherId).collection("mapObjectStatus").doc(mapObjectStatus.id).set(mapObjectStatus.toJSON())
        }

        batch.commit().then(() => {
          resolve();
        }).catch((error) => {
          reject(error);
        });
      });

      return promise;
    } else {
      return Promise.resolve();
    }
  }

  saveMapObjectStatus(publisherId, mapObjectStatus) {
    if (publisherId && mapObjectStatus) {
      const promise = new Promise((resolve, reject) => {
        if (!mapObjectStatus.id) {
          mapObjectStatus.id = UUIDUtils.generateUUID();
        }

        firestore.collection("publishers").doc(publisherId).collection("mapObjectStatus").doc(mapObjectStatus.id).set(mapObjectStatus.toJSON()).then(() => {
          resolve();
        }).catch((error) => {
          reject(error);
        });
      });

      return promise;
    } else {
      return Promise.resolve();
    }
  }

  deleteMapObjectStatus(publisherId, mapObjectStatusIds) {
    if (publisherId && mapObjectStatusIds) {
      const promise = new Promise((resolve, reject) => {
        const batch = firestore.batch();

        for (var mapObjectStatusId of mapObjectStatusIds) {
          firestore.collection("publishers").doc(publisherId).collection("mapObjectStatus").doc(mapObjectStatusId).delete();
        }

        batch.commit().then(() => {
          resolve();
        }).catch((error) => {
          reject(error);
        });
      });

      return promise;
    } else {
      return Promise.resolve();
    }
  }

  touchMapBundlesForMap(publisherId, map) {
    if (publisherId && map) {
      for (const bundleId in map.bundleIds) {
        if (map.bundleIds[bundleId]) {
          firestore.collection("publishers").doc(publisherId).collection("bundles").doc(bundleId).update({ mapObjectsChanged: true });
        }
      }
      return Promise.resolve();
    } else {
      return Promise.resolve();
    }
  }

  buildUrlToTilesRepository(bundleId, mapId, folder, imageFormat) {
    var url = this.buildUrlToMapBundle(bundleId) + "/" + folder + "{z}/{x}/{y}." + imageFormat;
    return url
  }

  buildUrlToMapBundle(bundleId) {
    return "https://s3.amazonaws.com/webviewer-prod-ondago/2.00/" + bundleId;
  }

  buildUrlToAsset(bundleId, assetFilePath) {
    if (assetFilePath != null && assetFilePath.indexOf("http://") < 0 && assetFilePath.indexOf("https://") < 0) {
      return "https://s3.amazonaws.com/webviewer-prod-ondago/2.00/" + bundleId + "/" + assetFilePath;
    } else {
      return assetFilePath;
    }
  }

  exportMapData(publisherId, mapId) {
    return new Promise((resolve, reject) => {
      this.loadMapObjectsForMap(publisherId, mapId).then((mapObjects) => {
        const jsonExportResult = [];
        const allPromises = [];

        for (const mapObject of mapObjects) {
          allPromises.push(
            new Promise((promiseResolve, promiseReject) => {
              firestore.collection("publishers").doc(publisherId).collection("mapObjects").doc(mapObject.id).collection("formFieldValues").get().then((formFieldValues) => {
                firestore.collection("publishers").doc(publisherId).collection("mapObjects").doc(mapObject.id).collection("media").get().then((medias) => {
                  const mapObjectJSON = {
                    id: mapObject.id,
                    createdBy: mapObject.createdBy,
                    latitude: mapObject.latLong ? mapObject.latLong.latitude : null,
                    longitude: mapObject.latLong ? mapObject.latLong.longitude : null,
                    poiType: mapObject.poiType,
                    name_en: mapObject.name ? mapObject.name["en"] : null,
                    name_fr: mapObject.name ? mapObject.name["fr"] : null
                  };

                  if (formFieldValues) {
                    formFieldValues.forEach((doc) => {
                      const formFieldValue = doc.data();
                      mapObjectJSON[formFieldValue.formFieldId] = formFieldValue.value;
                    });
                  }

                  const allMediaPromises = [];

                  if (medias) {
                    var index = 0;
                    medias.forEach((doc) => {
                      const media = doc.data();

                      const finalIndex = index;
                      allMediaPromises.push(
                        new Promise((mediaDownloadUrlResolve, mediaDownloadUrlReject) => {
                          const mediaPath = "publishers/" + publisherId + "/mapObjects/" + mapObject.id + "/media/" + media.filename;
                          storage.ref(mediaPath).getDownloadURL().then((url) => {
                            mapObjectJSON["media_" + finalIndex] = url + ".jpg";
                            mediaDownloadUrlResolve();
                          }).catch((error) => {
                            mediaDownloadUrlReject(error);
                          });
                        })
                      );

                      index++;
                    });
                  }

                  Promise.all(allMediaPromises).then(() => {
                    jsonExportResult.push(mapObjectJSON);
                    promiseResolve();
                  }).catch((error) => {
                    promiseReject(error);
                  });
                }).catch((error) => {
                  promiseReject(error);
                });
              }).catch((error) => {
                promiseReject(error);
              });
            })
          );
        }

        Promise.all(allPromises).then(() => {
          const columns = [];

          const csvExport = [];
          csvExport.push(columns);

          for (const json of jsonExportResult) {
            for (const key in json) {
              if (!columns.includes(key)) {
                columns.push(key);
              }
            }
          }

          for (const json of jsonExportResult) {
            const csvRecord = [];
            for (const column of columns) {
              const value = json[column];
              if (!value) {
                csvRecord.push("");
              } else {
                csvRecord.push(value);
              }
            }
            csvExport.push(csvRecord);
          }

          var stringExport = "";
          for (const csvRecord of csvExport) {
            for (const value of csvRecord) {
              stringExport += value + ",";
            }

            stringExport += "\n";
          }

          resolve(stringExport);
        }).catch((error) => {
          reject(error);
        });
      }).catch((error) => {
        reject(error);
      });
    });
  }

  async exportMapMedia(publisherId, mapId) {
    const exportPhotosFunction = functions.httpsCallable('exportMapObjectsMediaForMap')
    const result = await exportPhotosFunction({ publisherId, mapId })
    const { downloadUrl } = result.data
    const download = await fetch(downloadUrl)
    return download.blob()
  }

}
